import React from 'react';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';

const CommonExportButtons = ({
  data = [],
  exportHeaders = [],
  filename = 'export-data',
  pdfTitle = 'Export Data',
  totalText = 'Total Records',
  pdfOrientation = 'portrait', // 'portrait' or 'landscape'
  pdfHeaderColor = [41, 128, 185], // Default blue color, can be overridden
  includeTimestamp = false,
  customStyles = {},
  columnWidths = {}, // Object with column indexes and their widths
  dateFields = [], // Array of field paths that should be formatted as dates
  amountFields = [], // Array of field paths that should be formatted as currency amounts (divided by 100)
  priceFields = [], // Array of field paths that should be formatted as currency (no division)
}) => {
  
  const getValue = (obj, path, forPDF = false) => {
    const keys = path.split('.');
    let result = obj;
    for (const key of keys) {
      if (result === null || result === undefined) return 'N/A';
      result = result[key];
    }
    
    // Format price fields (simple currency formatting without division)
    if (priceFields.includes(path)) {
      if (result) {
        const formattedPrice = Number(result).toLocaleString();
        // For PDF, use 'Rs.' instead of rupee symbol to avoid character encoding issues
        return forPDF ? `Rs. ${formattedPrice}` : `₹ ${formattedPrice}`;
      }
      return 'N/A';
    }
    
    // Format amounts (currency amounts divided by 100 - like payment amounts)
    if (amountFields.includes(path) || 
        path.toLowerCase().includes('amount')) {
      if (result) {
        const formattedAmount = Number(result / 100).toLocaleString();
        // For PDF, use 'Rs.' instead of rupee symbol to avoid character encoding issues
        return forPDF ? `Rs. ${formattedAmount}` : `₹ ${formattedAmount}`;
      }
      return 'N/A';
    }
    
    // Auto-detect price fields based on field name patterns (for backward compatibility)
    if (path.toLowerCase().includes('price') || 
        path.toLowerCase().includes('cost')) {
      if (result) {
        const formattedPrice = Number(result).toLocaleString();
        // For PDF, use 'Rs.' instead of rupee symbol to avoid character encoding issues
        return forPDF ? `Rs. ${formattedPrice}` : `₹ ${formattedPrice}`;
      }
      return 'N/A';
    }
    
    // Format dates if the field is specified as a date field or contains date-like patterns
    if (dateFields.includes(path) || 
        path.includes('createdAt') || 
        path.includes('updatedAt') || 
        path.includes('Date') ||
        path.toLowerCase().includes('date')) {
      return result ? moment(result).format("DD/MM/YYYY, hh:mm A") : 'N/A';
    }
    
    return result || 'N/A';
  };

  const exportPDF = () => {
    const doc = new jsPDF(pdfOrientation);
    
    // Title
    doc.setFontSize(16);
    doc.text(pdfTitle, 14, 22);
    
    // Total count
    doc.setFontSize(12);
    doc.text(`${totalText}: ${data.length}`, 14, 32);
    
    // Timestamp if requested
    let startY = 42;
    if (includeTimestamp) {
      doc.text(`Generated on: ${moment().format("DD/MM/YYYY, hh:mm A")}`, 14, 42);
      startY = 52;
    } else {
      startY = 42;
    }
    
    // Prepare table configuration
    const tableConfig = {
      startY: startY,
      head: [exportHeaders.map((h) => h.label)],
      body: data.map((item) => exportHeaders.map((h) => getValue(item, h.key, true))),
      styles: { 
        fontSize: pdfOrientation === 'landscape' ? 8 : 9, 
        cellPadding: 3,
        overflow: 'linebreak',
        columnWidth: 'wrap',
        ...customStyles
      },
      headStyles: { 
        fontSize: pdfOrientation === 'landscape' ? 9 : 10, 
        fillColor: pdfHeaderColor,
        textColor: 255, 
        fontStyle: 'bold' 
      },
      margin: { top: 10 },
    };

    // Apply custom column widths if provided
    if (Object.keys(columnWidths).length > 0) {
      tableConfig.columnStyles = columnWidths;
    }
    
    autoTable(doc, tableConfig);
    doc.save(`${filename}.pdf`);
  };

  // Prepare CSV data with proper formatting
  const csvData = data.map((item) => {
    return exportHeaders.reduce((row, h) => {
      row[h.label] = getValue(item, h.key);
      return row;
    }, {});
  });

  return (
    <>
      <div className="d-flex mb-3 align-items-center justify-content-end">
        <CSVLink
          data={csvData}
          headers={exportHeaders.map(({ label }) => ({ label, key: label }))}
          filename={`${filename}.csv`}
          className="export-btn csv"
          title="Export CSV"
        >
          <i className="bi bi-file-earmark-spreadsheet-fill fs-5"></i>
        </CSVLink>
        <div className="export-divider"></div>
        <button onClick={exportPDF} className="export-btn pdf" title="Export PDF">
          <i className="bi bi-file-earmark-pdf-fill fs-5"></i>
        </button>
      </div>
    </>
  );
};

export default CommonExportButtons;